import { useAuth } from '@/src/context/AuthContext';
import { useTheme } from '@/src/context/ThemeContext';
import {
  acceptFriendRequestUseCase,
  declineFriendRequestUseCase,
  getPendingFriendRequestsUseCase,
} from '@/src/core/dependencies';
import { FriendshipWithUser } from '@/src/domain/models';
import { useTranslation } from '@/src/i18n';
import { FriendRequestsScreen } from '@/src/presentation/screens/FriendRequestsScreen';
import { useFocusEffect } from '@react-navigation/native';
import { useRouter } from 'expo-router';
import { useCallback, useState } from 'react';
import { platformAlert } from '@/src/core/utils/platformAlert';
import { logger } from '@/src/core/utils';

const PAGE_SIZE = 20;

export default function FriendRequestsContainer() {
  const router = useRouter();
  const { colors } = useTheme();
  const { t } = useTranslation();
  const { user } = useAuth();
  const [requests, setRequests] = useState<FriendshipWithUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(0);

  useFocusEffect(
    useCallback(() => {
      loadRequests();
    }, [])
  );

  const loadRequests = async () => {
    if (!user) return;

    try {
      setLoading(true);
      setPage(0);
      setHasMore(true);
      const data = await getPendingFriendRequestsUseCase.execute(user.id, PAGE_SIZE, 0);
      setRequests(data);
      setHasMore(data.length === PAGE_SIZE);
    } catch (error) {
      logger.error('FriendRequests', 'Failed to load friend requests', { error });
      platformAlert(t('common.error'), t('errors.failedToLoad'));
    } finally {
      setLoading(false);
    }
  };

  const loadMoreRequests = async () => {
    if (loadingMore || !hasMore || !user) return;

    try {
      setLoadingMore(true);
      const nextPage = page + 1;
      const data = await getPendingFriendRequestsUseCase.execute(
        user.id,
        PAGE_SIZE,
        nextPage * PAGE_SIZE
      );

      if (data.length > 0) {
        setRequests((prev) => [...prev, ...data]);
        setPage(nextPage);
        setHasMore(data.length === PAGE_SIZE);
      } else {
        setHasMore(false);
      }
    } catch (error) {
      logger.error('FriendRequests', 'Failed to load more friend requests', { error });
    } finally {
      setLoadingMore(false);
    }
  };

  const handleAccept = async (friendshipId: string) => {
    try {
      await acceptFriendRequestUseCase.execute(friendshipId);
      setRequests((prev) => prev.filter((r) => r.id !== friendshipId));
      platformAlert(t('common.success'), t('alerts.friendRequestAccepted'));
    } catch (error) {
      logger.error('FriendRequests', 'Failed to accept friend request', { error });
      platformAlert(t('common.error'), t('errors.failedToLoad'));
    }
  };

  const handleDecline = async (friendshipId: string) => {
    try {
      await declineFriendRequestUseCase.execute(friendshipId);
      setRequests((prev) => prev.filter((r) => r.id !== friendshipId));
      platformAlert(t('common.success'), t('alerts.friendRequestDeclined'));
    } catch (error) {
      logger.error('FriendRequests', 'Failed to decline friend request', { error });
      platformAlert(t('common.error'), t('errors.failedToLoad'));
    }
  };

  const handleUserPress = (userId: string) => {
    router.push(`/profile/${userId}`);
  };

  return (
    <FriendRequestsScreen
      requests={requests}
      loading={loading}
      loadingMore={loadingMore}
      onAccept={handleAccept}
      onDecline={handleDecline}
      onUserPress={handleUserPress}
      onRefresh={loadRequests}
      onLoadMore={loadMoreRequests}
      colors={colors}
    />
  );
}
